import React, { useEffect, useRef, useState } from "react";
import { Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Btn, P } from "../../AbstractElements";
import AnnexParamsForm from "./AnnexParamsForm";
import { previewDocument, signDocument } from "../../helpers/documentsApi";

/**
 * Подписание документа: параметры (если это приложение) → текст → подпись.
 *
 * Подписывается ровно тот текст, который показан: подпись отправляется по коду
 * документа с теми же параметрами, из которых собран предпросмотр.
 */
const SignModal = ({ template, formats, keyActions, companyId, onClose, onSigned }) => {
  const isAnnex = template && template.is_annex;

  const [step, setStep] = useState(isAnnex ? "params" : "text");
  const [params, setParams] = useState({ formats: [], locations: [] });
  const [html, setHtml] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState("");

  const textRef = useRef(null);

  const loadPreview = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await previewDocument(template.code, params, companyId);
      setHtml(data.html || "");
      setStep("text");
      setScrolledToEnd(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step === "text" && !html) {
      loadPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Кнопка подписания открывается, только когда текст пролистан до конца:
  // «ознакомился с документом» должно означать хотя бы это.
  const handleScroll = () => {
    const el = textRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      setScrolledToEnd(true);
    }
  };

  const handleSign = async () => {
    setSigning(true);
    setError("");
    try {
      const doc = await signDocument(template.code, params, companyId);
      if (onSigned) onSigned(doc);
    } catch (e) {
      setError(e.message);
    } finally {
      setSigning(false);
    }
  };

  return (
    <Modal isOpen={true} toggle={onClose} size="lg" centered scrollable>
      <ModalHeader toggle={onClose}>{template.title}</ModalHeader>
      <ModalBody>
        {step === "params" && (
          <>
            <P className="text-muted">
              Заполните параметры кампании — они попадут в текст приложения.
            </P>
            <AnnexParamsForm
              values={params}
              formats={formats}
              keyActions={keyActions}
              onChange={setParams}
            />
          </>
        )}

        {step === "text" && (
          <>
            {loading && <P>Готовим документ…</P>}
            {!loading && (
              <div
                ref={textRef}
                onScroll={handleScroll}
                className="border rounded p-3"
                style={{ maxHeight: "50vh", overflowY: "auto", background: "#fff" }}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            )}
            {!loading && (
              <div className="form-check mt-3">
                <Input
                  type="checkbox"
                  id="doc-accept"
                  className="form-check-input"
                  checked={accepted}
                  disabled={!scrolledToEnd}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
                <Label className="form-check-label" for="doc-accept">
                  Я прочитал документ, согласен с его условиями и подтверждаю
                  достоверность указанных реквизитов
                </Label>
                {!scrolledToEnd && (
                  <P className="text-muted mb-0">
                    Пролистайте текст до конца, чтобы подписать.
                  </P>
                )}
              </div>
            )}
          </>
        )}

        {error && <P className="text-danger mt-2">{error}</P>}
      </ModalBody>
      <ModalFooter>
        {step === "params" && (
          <Btn attrBtn={{ color: "primary", disabled: loading, onClick: loadPreview }}>
            {loading ? "Готовим…" : "Дальше"}
          </Btn>
        )}
        {step === "text" && (
          <>
            {isAnnex && (
              <Btn
                attrBtn={{
                  color: "light",
                  onClick: () => {
                    setHtml("");
                    setAccepted(false);
                    setStep("params");
                  },
                }}
              >
                Назад
              </Btn>
            )}
            <Btn
              attrBtn={{
                color: "primary",
                disabled: !accepted || signing,
                onClick: handleSign,
              }}
            >
              {signing ? "Подписываем…" : "Подписать"}
            </Btn>
          </>
        )}
        <Btn attrBtn={{ color: "light", onClick: onClose }}>Отмена</Btn>
      </ModalFooter>
    </Modal>
  );
};

export default SignModal;
